import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  listStudentQuestionAttempts,
  submitStudentQuestionAttempt,
} from "../../../../api/studentQuestions"
import type { Attempt, AttemptStatus } from "../types"
import { isTerminalAttempt } from "../utils/attemptStatus"

const POLL_MS = 2500

const asAttempt = (raw: Attempt): Attempt => ({
  ...raw,
  answerText: raw.answerText || "",
  criteriaBreakdown: raw.criteriaBreakdown ?? null,
  confidence: raw.confidence ?? null,
  assessmentError: raw.assessmentError ?? null,
})

const useQuestionAttempts = (
  questionId: string | null,
  options: { maxScore: number; maxAttempts?: number }
) => {
  const maxAttempts = options.maxAttempts ?? 3
  const [serverAttempts, setServerAttempts] = useState<Attempt[]>([])
  const [draft, setDraft] = useState<Attempt | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const submittingRef = useRef(false)

  const load = useCallback(async () => {
    if (!questionId) {
      setServerAttempts([])
      return
    }
    try {
      const res = await listStudentQuestionAttempts(questionId)
      const rows = Array.isArray(res.data?.data) ? res.data.data : []
      setServerAttempts(rows.map(asAttempt))
      setLoadError(null)
    } catch {
      setLoadError("Could not load attempts")
    }
  }, [questionId])

  useEffect(() => {
    setDraft(null)
    void load()
  }, [load])

  const attempts = useMemo(() => {
    if (!draft) return serverAttempts
    return [...serverAttempts, draft]
  }, [serverAttempts, draft])

  const latest = attempts[attempts.length - 1] ?? null
  const isCompleted = useMemo(
    () => attempts.some((a) => isTerminalAttempt(a.status)),
    [attempts]
  )

  useEffect(() => {
    const marking = serverAttempts.some((a) => a.status === "marking")
    if (!questionId || !marking) return undefined
    const timer = window.setInterval(() => {
      void load()
    }, POLL_MS)
    return () => window.clearInterval(timer)
  }, [questionId, serverAttempts, load])

  const startAttempt = useCallback(() => {
    if (!questionId) return
    if (attempts.length >= maxAttempts) return
    if (latest && !isTerminalAttempt(latest.status) && latest.status !== "draft") {
      return
    }
    if (latest?.status === "draft") return

    setDraft({
      id: `${questionId}-draft`,
      attemptNumber: attempts.length + 1,
      answerText: "",
      startedAt: new Date().toISOString(),
      submittedAt: null,
      timeSpentSeconds: 0,
      status: "draft",
      score: null,
      maxScore: options.maxScore || null,
      criteriaBreakdown: null,
      confidence: null,
    })
  }, [questionId, attempts, latest, maxAttempts, options.maxScore])

  const updateDraft = useCallback((attemptId: string, answerText: string) => {
    setDraft((current) =>
      current && current.id === attemptId && current.status === "draft"
        ? { ...current, answerText }
        : current
    )
  }, [])

  const submitAttempt = useCallback(
    async (attemptId: string) => {
      if (!questionId || !draft || draft.id !== attemptId || submittingRef.current) {
        return
      }
      submittingRef.current = true
      const started = Date.parse(draft.startedAt)
      const elapsed = Number.isFinite(started)
        ? Math.max(0, Math.round((Date.now() - started) / 1000))
        : 0
      setDraft((current) =>
        current
          ? { ...current, status: "marking" as AttemptStatus, submittedAt: new Date().toISOString() }
          : current
      )
      try {
        const res = await submitStudentQuestionAttempt(questionId, {
          answerText: draft.answerText,
          timeSpentSeconds: elapsed,
        })
        const saved = res.data?.data
        setDraft(null)
        if (saved) {
          setServerAttempts((prev) => {
            if (prev.some((item) => item.id === saved.id)) {
              return prev.map((item) => (item.id === saved.id ? asAttempt(saved) : item))
            }
            return [...prev, asAttempt(saved)]
          })
        } else {
          await load()
        }
      } catch {
        setDraft((current) =>
          current
            ? {
                ...current,
                status: "failed" as AttemptStatus,
                assessmentError: "Could not submit this attempt for marking.",
              }
            : current
        )
      } finally {
        submittingRef.current = false
      }
    },
    [questionId, draft, load]
  )

  return {
    attempts,
    latest,
    isCompleted,
    maxAttempts,
    loadError,
    canStartNew:
      !!questionId &&
      attempts.length < maxAttempts &&
      (!latest || isTerminalAttempt(latest.status)),
    startAttempt,
    updateDraft,
    submitAttempt,
    refresh: load,
  }
}

export default useQuestionAttempts
